import React from 'react';
import ReactDOMServer from 'react-dom/server';
import ArticleCard from '../components/ArticleCard';
import PartnerCard from '../components/PartnerCard';
import NewsCard from '../components/NewsCard';
import TestimonialCard from '../components/TestimonialCard';
import VideoCard from '../components/VideoCard';
import TodoPlanCard from '../components/TodoPlanCard';
import CoachCard from '../components/CoachCard';
import DisclaimerBanner from '../components/DisclaimerBanner';
import Tip from '../components/Tip';
import CelebrationBubble from '../components/CelebrationBubble';
import { getPrimaryMission } from './missionUtils';
import { stripMITechniqueLabels } from './miTechniques';

/**
 * Format Claude response text with markdown-like syntax and content cards
 * Converts:
 * - **bold** to <strong>
 * - *italic* to <em>
 * - <Article:{JSON}> to ArticleCard components
 * - <Partner:{JSON}> to PartnerCard components
 * - <News:{JSON}> to NewsCard components
 * - <Testimonial:{JSON}> to TestimonialCard components
 * - <TodoPlan:{JSON}> to TodoPlanCard component (special case, parsed separately)
 * - <TodoWidget:{JSON}> to Todo component widgets (parsed separately)
 * Note: &MI technique& content is stripped on backend, not shown to user
 */
/**
 * Escape HTML special chars so untrusted assistant/LLM text can't inject markup
 * (e.g. <img onerror=...>) when the formatted string is later assigned via
 * dangerouslySetInnerHTML / innerHTML. Applied to the free text only — the
 * content-card HTML is produced by React (renderToStaticMarkup) and is already
 * escaped, so it is substituted in AFTER this step.
 */
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function formatResponse(text, conversationId = null, currentMission = null, onPlanApprove = null, onPlanEdit = null) {
  let formatted = text;
  // Rendered card HTML (trusted, React-escaped) keyed by the placeholder token
  // that currently stands in for it inside `formatted`. Substituted in only
  // after the surrounding free text has been HTML-escaped.
  const cardHtmlByToken = new Map();

  // First, extract TodoPlan sections (they contain JSON objects with objective and tiers)
  const todoPlanRegex = /<TodoPlan:(\{[\s\S]*?\})>/g;
  const todoPlanMatches = [];
  let tempText = text;
  let match;
  let matchIndex = 0;

  while ((match = todoPlanRegex.exec(text)) !== null) {
    const placeholder = `__TODOPLAN_${matchIndex}__`;
    todoPlanMatches.push({
      placeholder,
      fullMatch: match[0],
      jsonString: match[1]
    });
    tempText = tempText.replace(match[0], placeholder);
    matchIndex++;
  }

  // Strip MI technique labels (both {{label}} and &label& formats)
  tempText = stripMITechniqueLabels(
    tempText
      .replace(/\*\*\{\{[^}]*\}\}\*\*/g, '')  // **{{bold labels}}**
      .replace(/\{\{[^}]*\}\}/g, '')           // {{labels}}
  ).trim();

  // Restore TodoPlan sections
  formatted = tempText;

  // Parse and render TodoPlan components
  const todoPlans = [];
  todoPlanMatches.forEach((planMatch) => {
    try {
      const planData = JSON.parse(planMatch.jsonString);
      // planData is now { objective: "...", tiers: [...] }
      todoPlans.push({
        placeholder: planMatch.placeholder,
        objective: planData.objective,
        tiers: planData.tiers
      });
    } catch (error) {
      console.error('[formatResponse] Failed to parse TodoPlan JSON:', error);
      console.error('[formatResponse] JSON string was:', planMatch.jsonString);
      // Keep the placeholder if parsing fails
    }
  });

  // Replace TodoPlan placeholders with rendered TodoPlanCard components
  todoPlans.forEach((planData) => {
    const card = <TodoPlanCard
      plan={planData.tiers}
      objective={planData.objective}
      oldObjective={getPrimaryMission(currentMission)}
      conversationId={conversationId}
      onApprove={onPlanApprove}
      onEdit={onPlanEdit}
    />;
    // Defer insertion: keep the placeholder in `formatted` so the free text can
    // be HTML-escaped first, then swap the placeholder for this trusted markup.
    cardHtmlByToken.set(planData.placeholder, ReactDOMServer.renderToStaticMarkup(card));
  });

  // Strip <Notification:...> tags (server-only, should never reach frontend but just in case)
  formatted = formatted.replace(/<Notification:(\{[^<>]*?\})>/g, '');

  // Parse content tags with JSON data: <Article:{...}>, <Partner:{...}>, <CoachCard:{...}>, <Quiz:{...}>, etc.
  // Match pattern: <Type:{JSON}> where Type is Article, Partner, News, Testimonial, Video, CoachCard, or Quiz
  const contentTagRegex = /<(Article|Partner|News|Testimonial|Video|CoachCard|Quiz|Disclaimer|Tip|Celebration):(\{[^<>]*?\})>/g;
  const contentCards = [];
  let contentMatch;

  while ((contentMatch = contentTagRegex.exec(text)) !== null) {
    const tagType = contentMatch[1];
    const jsonString = contentMatch[2];

    console.log('[formatResponse] Found content tag:', tagType, 'at position', contentMatch.index);
    console.log('[formatResponse] JSON string length:', jsonString.length);

    try {
      const data = JSON.parse(jsonString);

      // Skip empty tags (e.g., <Article:{}>) — EXCEPT Disclaimer, whose
      // canonical form IS the empty payload (<Disclaimer:{}>)
      if (Object.keys(data).length === 0 && tagType !== 'Disclaimer') {
        console.log('[formatResponse] Skipping empty tag:', tagType);
        // Remove the tag from the output
        formatted = formatted.replace(contentMatch[0], '');
        continue;
      }

      console.log('[formatResponse] Successfully parsed', tagType, 'with', Object.keys(data).length, 'fields');

      contentCards.push({
        placeholder: contentMatch[0],
        type: tagType,
        data: data
      });
    } catch (error) {
      console.error('[formatResponse] Failed to parse content tag JSON:', error);
      console.error('[formatResponse] Tag type:', tagType, 'JSON:', jsonString);
      // Keep the placeholder if parsing fails
    }
  }

  // Replace content tags with rendered card components
  contentCards.forEach((cardData, cardIdx) => {
    let card;

    switch (cardData.type) {
      case 'Article':
        card = <ArticleCard
          title={cardData.data.title}
          slug={cardData.data.slug}
          category={cardData.data.category}
          heroImage={cardData.data.heroImage}
          excerpt={cardData.data.excerpt || cardData.data.description}
        />;
        break;

      case 'Partner':
        card = <PartnerCard
          name={cardData.data.name}
          websiteUrl={cardData.data.websiteUrl}
          bestFor={cardData.data.bestFor}
          imageUrl={cardData.data.imageUrl}
          description={cardData.data.description}
        />;
        break;

      case 'News':
        card = <NewsCard
          headline={cardData.data.headline}
          sourceUrl={cardData.data.sourceUrl}
          publishedDate={cardData.data.publishedDate}
          heroImage={cardData.data.heroImage}
          summary={cardData.data.summary}
          source={cardData.data.source}
          id={cardData.data.id}
        />;
        break;

      case 'Testimonial':
        card = <TestimonialCard
          userName={cardData.data.userName}
          achievement={cardData.data.achievement}
          testimonialText={cardData.data.testimonialText}
          imageUrl={cardData.data.imageUrl}
          date={cardData.data.date}
        />;
        break;

      case 'Video':
        card = <VideoCard
          id={cardData.data.id}
          videoId={cardData.data.videoId}
          title={cardData.data.title}
          courseName={cardData.data.courseName}
          description={cardData.data.description}
          videoUrl={cardData.data.videoUrl}
          lessonUrl={cardData.data.lessonUrl}
          thumbnailUrl={cardData.data.thumbnailUrl}
        />;
        break;

      case 'CoachCard':
        card = <CoachCard
          id={cardData.data.id}
          displayName={cardData.data.displayName}
          bio={cardData.data.bio}
          specializations={cardData.data.specializations}
          sessionDuration={cardData.data.sessionDuration}
          profileImage={cardData.data.profileImage}
          similarity={cardData.data.similarity}
        />;
        break;

      case 'Disclaimer':
        card = <DisclaimerBanner />;
        break;

      case 'Tip':
        card = <Tip text={cardData.data.text} />;
        break;

      case 'Celebration':
        card = <CelebrationBubble factType={cardData.data.factType} message={cardData.data.message} progress={cardData.data.progress} />;
        break;

      default:
        console.warn('[formatResponse] Unknown content card type:', cardData.type);
        return;
    }

    // Defer insertion behind a placeholder token so the free text can be
    // escaped first (split/join avoids `$` being treated specially).
    const token = `__CONTENTCARD_${cardIdx}__`;
    cardHtmlByToken.set(token, ReactDOMServer.renderToStaticMarkup(card));
    formatted = formatted.split(cardData.placeholder).join(token);
  });

  // Drop any tag-shaped leftovers — a numbered ref the server failed to resolve
  // (<Article:1>), a content tag whose JSON didn't parse, stray HTML. Every tag
  // we DO render is already an inert placeholder token by now, so whatever
  // still looks like <Word...> must never reach the bubble as literal text.
  // Requires a letter right after "<" so prose like "x < 5 and y > 3" survives.
  formatted = formatted
    .replace(/<\/?[A-Za-z][^<>]*>/g, '')
    .replace(/[ \t]+$/gm, '');

  // Everything still in `formatted` at this point is untrusted free text plus
  // inert placeholder tokens. Escape it so no raw HTML from the assistant/LLM
  // (e.g. <img onerror=...>) survives into the DOM, THEN swap the tokens for
  // the trusted, React-escaped card markup.
  formatted = escapeHtml(formatted);
  for (const [token, html] of cardHtmlByToken) {
    formatted = formatted.split(token).join(html);
  }

  // Format bold: **text** -> <strong>
  formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Format italic: *text* -> <em>
  formatted = formatted.replace(/\*([^*]+)\*/g, '<em style="color: #6e8e87; font-style: italic;">$1</em>');

  // Convert newlines to <br>
  formatted = formatted.replace(/\n\n/g, '<br><br>');
  formatted = formatted.replace(/\n/g, '<br>');

  return formatted;
}

/**
 * Check if a message contains a TodoPlan
 */
export function hasTodoPlan(text) {
  return /<TodoPlan:/.test(text);
}

/**
 * Parse TodoPlan from message content
 * Returns the plan object { objective, tiers } if found, null otherwise
 */
export function parseTodoPlan(text) {
  const todoPlanRegex = /<TodoPlan:(\{[\s\S]*?\})>/;
  const match = text.match(todoPlanRegex);

  if (!match) {
    return null;
  }

  try {
    const planData = JSON.parse(match[1]);
    // Return the full object with objective and tiers
    return planData;
  } catch (error) {
    console.error('[parseTodoPlan] Failed to parse TodoPlan JSON:', error);
    console.error('[parseTodoPlan] JSON string was:', match[1]);
    return null;
  }
}

/**
 * Parse content that may contain interactive tags (TodoWidget, Article, News, etc.)
 * Returns { hasInteractive: boolean, parts: [{ type: 'text'|'widget'|'article'|'news'|'testimonial'|'partner', content/data }] }
 */
export function parseContentWithWidgets(text) {
  console.log('[parseContentWithWidgets] Parsing text with length:', text.length);

  // Match all interactive content tags: <TodoWidget:{...}>, <Article:{...}>, <News:{...}>, <CoachCard:{...}>, <Quiz:{...}>, etc.
  const interactiveRegex = /<(TodoWidget|Article|News|Testimonial|Partner|Video|CoachCard|Quiz|ProfileQuestion|Disclaimer|Tip|Celebration):(\{[^<>]*\})>/g;
  const parts = [];
  let lastIndex = 0;
  let hasInteractive = false;
  let match;
  const seenCards = new Set();

  // Strip MI technique labels (both {{label}} and &label& formats)
  const cleanedText = stripMITechniqueLabels(
    text
      .replace(/\*\*\{\{[^}]*\}\}\*\*/g, '')  // **{{bold labels}}**
      .replace(/\{\{[^}]*\}\}/g, '')           // {{labels}}
  ).trim();
  console.log('[parseContentWithWidgets] Cleaned text length:', cleanedText.length);

  while ((match = interactiveRegex.exec(cleanedText)) !== null) {
    console.log('[parseContentWithWidgets] Found tag:', match[1], 'at position', match.index);
    const tagType = match[1]; // TodoWidget, Article, News, etc.
    const jsonString = match[2];

    // Add text before the tag
    if (match.index > lastIndex) {
      const textContent = cleanedText.substring(lastIndex, match.index);
      if (textContent.trim()) {
        parts.push({
          type: 'text',
          content: textContent
        });
      }
    }

    // Parse and add the interactive component
    try {
      const data = JSON.parse(jsonString);

      // Skip empty tags (e.g., <Article:{}>) — EXCEPT Disclaimer, whose
      // canonical form IS the empty payload (<Disclaimer:{}>)
      if (Object.keys(data).length === 0 && tagType !== 'Disclaimer') {
        lastIndex = match.index + match[0].length;
        continue;
      }

      // Never render the same card twice in one message (the model can repeat
      // a tag, or the server can append one the model already included).
      // Keyed on slug/id when present so a copy whose JSON differs only in
      // e.g. the excerpt still counts as the same item.
      const cardKey = `${tagType}:${data.slug ?? data.id ?? jsonString}`;
      if (seenCards.has(cardKey)) {
        lastIndex = match.index + match[0].length;
        continue;
      }
      seenCards.add(cardKey);

      hasInteractive = true;

      // Map tag type to part type
      const typeMap = {
        'TodoWidget': 'widget',
        'Article': 'article',
        'News': 'news',
        'Testimonial': 'testimonial',
        'Partner': 'partner',
        'Video': 'video',
        'CoachCard': 'coach',
        'Quiz': 'quiz',
        'ProfileQuestion': 'profilequestion',
        'Disclaimer': 'disclaimer',
        'Tip': 'tip',
        'Celebration': 'celebration'
      };

      parts.push({
        type: typeMap[tagType] || 'unknown',
        data: data
      });
    } catch (error) {
      console.error('[parseContentWithWidgets] Failed to parse tag JSON:', error);
      console.error('[parseContentWithWidgets] Tag type:', tagType, 'JSON:', jsonString);
      // If parsing fails, treat it as text
      parts.push({
        type: 'text',
        content: match[0]
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last tag
  if (lastIndex < cleanedText.length) {
    const textContent = cleanedText.substring(lastIndex);
    if (textContent.trim()) {
      parts.push({
        type: 'text',
        content: textContent
      });
    }
  }

  console.log('[parseContentWithWidgets] Finished parsing. Has interactive:', hasInteractive, 'Parts:', parts.length);

  return {
    hasWidgets: hasInteractive, // Keep old name for backward compatibility
    parts: hasInteractive ? parts : [{ type: 'text', content: cleanedText }]
  };
}
